/**
 * Client-side analytics wrapper.
 * @package @app/analytics/client
 *
 * Thin typed wrapper around `posthog-js`. Use from React components.
 * The posthog-js SDK is initialized in apps/www/app/routes/__root.tsx;
 * this module imports the same singleton instance.
 */

import posthog from 'posthog-js';
import type { EventMap, EventName, UserProperties } from './events';

const isBrowser = typeof window !== 'undefined';

function isReady(): boolean {
  if (!isBrowser) return false;
  // posthog-js exposes __loaded after init() completes. Reading via guard
  // because the type definition doesn't expose this internal field.
  return Boolean((posthog as unknown as { __loaded?: boolean }).__loaded);
}

/**
 * Capture an event client-side. Type-safe via EventMap.
 *
 * @example
 * track('pricing_cta_clicked', { plan: 'pro', billing_interval: 'monthly', ... });
 */
export function track<E extends EventName>(event: E, properties: EventMap[E]): void {
  if (!isReady()) return;
  try {
    posthog.capture(event, properties as unknown as Record<string, unknown>);
  } catch (err) {
    console.error('[analytics] track failed:', err);
  }
}

/**
 * Identify the current user. Call after sign-in (magic-link verify or OAuth
 * callback). Safe to call multiple times — PostHog dedupes.
 *
 * @param distinctId Must be `user.id` (stable across email changes). Never
 * use email as distinct_id.
 */
export function identify(distinctId: string, properties: UserProperties): void {
  if (!isReady()) return;
  try {
    posthog.identify(distinctId, properties as unknown as Record<string, unknown>);
  } catch (err) {
    console.error('[analytics] identify failed:', err);
  }
}

/**
 * Clear identity and start a fresh anonymous session. Call on sign-out so
 * the next user on a shared device doesn't inherit the previous identity.
 */
export function reset(): void {
  if (!isReady()) return;
  try {
    posthog.reset();
  } catch (err) {
    console.error('[analytics] reset failed:', err);
  }
}
